import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useChatSocket — connects to /api/ws/chat/{roomId} with auto-reconnect.
 *
 * @param {string|null} roomId
 * @param {object} handlers { onMessage, onPresence, onTyping }
 * @returns { send, sendTyping, markRead, status, onlineUsers }
 */
export default function useChatSocket(roomId, handlers = {}) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const closingRef = useRef(false);

  const [status, setStatus] = useState("idle"); // idle | connecting | open | closed
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  const connect = useCallback(() => {
    if (!roomId) return;
    closingRef.current = false;
    setStatus("connecting");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const httpUrl = new URL(base);
    const wsProto = httpUrl.protocol === "https:" ? "wss:" : "ws:";
    const url = `${wsProto}//${httpUrl.host}/api/ws/chat/${encodeURIComponent(
      roomId
    )}`;
    let socket;
    try {
      socket = new WebSocket(url);
    } catch (err) {
      setStatus("closed");
      scheduleReconnect();
      return;
    }
    wsRef.current = socket;

    socket.onopen = () => {
      setStatus("open");
      // keep-alive ping every 25s
      pingTimer.current = setInterval(() => {
        try {
          socket.send(JSON.stringify({ type: "ping" }));
        } catch (_) {
          /* swallow */
        }
      }, 25000);
    };

    socket.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (payload.type === "pong") return;
      if (payload.type === "presence") {
        setOnlineUsers((prev) => {
          const next = new Set(prev);
          if (payload.online) next.add(payload.user_id);
          else next.delete(payload.user_id);
          return next;
        });
        handlersRef.current.onPresence?.(payload);
        return;
      }
      if (payload.type === "message") {
        handlersRef.current.onMessage?.(payload.data);
        return;
      }
      if (payload.type === "typing") {
        handlersRef.current.onTyping?.(payload);
        return;
      }
      if (payload.type === "error") {
        handlersRef.current.onError?.(payload);
        return;
      }
    };

    socket.onclose = () => {
      setStatus("closed");
      if (pingTimer.current) {
        clearInterval(pingTimer.current);
        pingTimer.current = null;
      }
      if (!closingRef.current) scheduleReconnect();
    };

    socket.onerror = () => {
      try {
        socket.close();
      } catch (_) {
        /* swallow */
      }
    };
  }, [roomId]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimer.current) return;
    reconnectTimer.current = setTimeout(() => {
      reconnectTimer.current = null;
      connect();
    }, 2500);
  }, [connect]);

  useEffect(() => {
    if (!roomId) return undefined;
    connect();
    return () => {
      closingRef.current = true;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (pingTimer.current) {
        clearInterval(pingTimer.current);
        pingTimer.current = null;
      }
      try {
        wsRef.current?.close();
      } catch (_) {
        /* swallow */
      }
      wsRef.current = null;
      setOnlineUsers(new Set());
      setStatus("idle");
    };
  }, [roomId, connect]);

  const safeSend = useCallback((data) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(data));
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  const send = useCallback(
    (content) => safeSend({ type: "message", content }),
    [safeSend]
  );
  const sendTyping = useCallback(() => safeSend({ type: "typing" }), [safeSend]);
  const markRead = useCallback(() => safeSend({ type: "read" }), [safeSend]);

  return { status, onlineUsers, send, sendTyping, markRead };
}
